import {getWriteStore} from './writeStore'
import {ProtocolWriteDrainer} from './protocolWriteDrainer'

/**
 * Process-wide registry of ProtocolWriteDrainer instances, one per
 * (companyId, protocolId). Ref-counted so multiple components can
 * acquire the same drainer (HMR, concurrent panels) and it only stops
 * when the last consumer releases.
 *
 * In the single-window neurotune app this is functionally equivalent
 * to per-mount drainers, but it makes drainer identity stable across
 * remounts and removes the "two drainers racing for the same queue"
 * failure mode.
 */

const drainers = new Map<string, ProtocolWriteDrainer>()
const refCounts = new Map<string, number>()

function key(companyId: string, protocolId: string): string {
    return `${companyId}:${protocolId}`
}

export function acquireProtocolWriteDrainer(companyId: string, protocolId: string): ProtocolWriteDrainer {
    const k = key(companyId, protocolId)
    let drainer = drainers.get(k)
    if (!drainer) {
        drainer = new ProtocolWriteDrainer(companyId, protocolId, getWriteStore())
        drainers.set(k, drainer)
    }
    refCounts.set(k, (refCounts.get(k) ?? 0) + 1)
    return drainer
}

export function releaseProtocolWriteDrainer(companyId: string, protocolId: string): void {
    const k = key(companyId, protocolId)
    const next = (refCounts.get(k) ?? 0) - 1
    if (next <= 0) {
        drainers.get(k)?.stop()
        drainers.delete(k)
        refCounts.delete(k)
    } else {
        refCounts.set(k, next)
    }
}

/** Test helper: stop and forget all registered drainers. */
export function __resetProtocolWriteDrainerRegistry(): void {
    for (const d of drainers.values()) d.stop()
    drainers.clear()
    refCounts.clear()
}
